import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import QuickActionButton from '../../components/ui/QuickActionButton';
import MetricCard from './components/MetricCard';
import ProgressChart from './components/ProgressChart';
import GoalProgressCard from './components/GoalProgressCard';
import PersonalRecordCard from './components/PersonalRecordCard';
import BodyMeasurementTracker from './components/BodyMeasurementTracker';
import WorkoutStreakCard from './components/WorkoutStreakCard';
import FilterPanel from './components/FilterPanel';
import AchievementBadge from './components/AchievementBadge';

const ProgressAnalytics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('all');

  const keyMetrics = [
  {
    icon: 'Dumbbell',
    label: 'Total Workouts',
    value: '127',
    change: '+12%',
    changeType: 'positive',
    trend: 'up',
    color: 'var(--color-accent)'
  },
  {
    icon: 'TrendingUp',
    label: 'Strength Gain',
    value: '18',
    unit: '%',
    change: '+5%',
    changeType: 'positive',
    trend: 'up',
    color: 'var(--color-success)'
  },
  {
    icon: 'Flame',
    label: 'Calories Burned',
    value: '42.5',
    unit: 'k',
    change: '+8%',
    changeType: 'positive',
    trend: 'up',
    color: 'var(--color-warning)'
  },
  {
    icon: 'Target',
    label: 'Goals Achieved',
    value: '8',
    unit: '/10',
    change: '80%',
    changeType: 'positive',
    trend: 'up',
    color: 'var(--color-primary)'
  }];


  const workoutFrequencyData = [
  { name: 'Week 1', workouts: 4, goal: 5 },
  { name: 'Week 2', workouts: 5, goal: 5 },
  { name: 'Week 3', workouts: 3, goal: 5 },
  { name: 'Week 4', workouts: 6, goal: 5 }];


  const strengthProgressData = [
  { name: 'Jan', benchPress: 135, squat: 185, deadlift: 225 },
  { name: 'Feb', benchPress: 145, squat: 195, deadlift: 245 },
  { name: 'Mar', benchPress: 155, squat: 205, deadlift: 265 },
  { name: 'Apr', benchPress: 165, squat: 215, deadlift: 285 },
  { name: 'May', benchPress: 175, squat: 225, deadlift: 305 },
  { name: 'Jun', benchPress: 185, squat: 235, deadlift: 325 }];


  const bodyCompositionData = [
  { name: 'Jan', weight: 185, bodyFat: 22 },
  { name: 'Feb', weight: 183, bodyFat: 21 },
  { name: 'Mar', weight: 181, bodyFat: 20 },
  { name: 'Apr', weight: 179, bodyFat: 19 },
  { name: 'May', weight: 177, bodyFat: 18 },
  { name: 'Jun', weight: 175, bodyFat: 17 }];


  const goals = [
  {
    title: 'Bench Press 200 lbs',
    current: 185,
    target: 200,
    unit: ' lbs',
    icon: 'Dumbbell',
    deadline: 'Aug 15, 2026',
    status: 'in-progress'
  },
  {
    title: 'Run 5K Under 25 Minutes',
    current: 27,
    target: 25,
    unit: ' min',
    icon: 'Timer',
    deadline: 'Jul 30, 2026',
    status: 'at-risk'
  },
  {
    title: 'Lose 10 lbs Body Fat',
    current: 8,
    target: 10,
    unit: ' lbs',
    icon: 'TrendingDown',
    deadline: 'Sep 1, 2026',
    status: 'in-progress'
  },
  {
    title: '100 Consecutive Workouts',
    current: 100,
    target: 100,
    unit: '',
    icon: 'Award',
    deadline: 'Jun 20, 2026',
    status: 'completed'
  }];


  const personalRecords = [
  {
    exercise: 'Bench Press',
    record: 185,
    unit: 'lbs',
    date: 'Jun 18, 2026',
    improvement: '15 lbs',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1585150e8-1767770922804.png",
    imageAlt: 'Athletic man performing bench press exercise with barbell in modern gym with focused expression and proper form'
  },
  {
    exercise: 'Squat',
    record: 235,
    unit: 'lbs',
    date: 'Jun 15, 2026',
    improvement: '20 lbs',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16ff5687b-1768283918521.png",
    imageAlt: 'Fit woman performing barbell squat exercise in gym with proper depth and form wearing athletic attire'
  },
  {
    exercise: 'Deadlift',
    record: 325,
    unit: 'lbs',
    date: 'Jun 12, 2026',
    improvement: '25 lbs',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_103e47251-1766916932210.png",
    imageAlt: 'Strong athlete executing deadlift with heavy barbell in professional gym setting with chalk dust visible'
  },
  {
    exercise: '5K Run',
    record: 27,
    unit: 'min',
    date: 'Jun 10, 2026',
    improvement: '2 min',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_153ef96b5-1768346650161.png",
    imageAlt: 'Runner in motion on outdoor track during sunrise with athletic gear and determined expression'
  }];


  const bodyMeasurements = [
  {
    label: 'Weight',
    value: '175 lbs',
    date: 'Jun 20, 2026',
    icon: 'Scale',
    change: '-2 lbs',
    changeType: 'positive'
  },
  {
    label: 'Body Fat',
    value: '17%',
    date: 'Jun 20, 2026',
    icon: 'Percent',
    change: '-1%',
    changeType: 'positive'
  },
  {
    label: 'Chest',
    value: '42 in',
    date: 'Jun 15, 2026',
    icon: 'Maximize2',
    change: '+0.5 in',
    changeType: 'positive'
  },
  {
    label: 'Waist',
    value: '32 in',
    date: 'Jun 15, 2026',
    icon: 'Minimize2',
    change: '-1 in',
    changeType: 'positive'
  },
  {
    label: 'Arms',
    value: '15 in',
    date: 'Jun 15, 2026',
    icon: 'Move',
    change: '+0.3 in',
    changeType: 'positive'
  },
  {
    label: 'Thighs',
    value: '24 in',
    date: 'Jun 15, 2026',
    icon: 'Move',
    change: '+0.4 in',
    changeType: 'positive'
  }];


  const achievements = [
  {
    title: '100 Day Streak',
    description: 'Completed 100 consecutive days of workouts without missing a single session',
    icon: 'Flame',
    date: 'Jun 20, 2026',
    rarity: 'legendary'
  },
  {
    title: 'Strength Master',
    description: 'Achieved personal records in all major compound lifts within the same month',
    icon: 'Trophy',
    date: 'Jun 18, 2026',
    rarity: 'epic'
  },
  {
    title: 'Early Bird',
    description: 'Completed 50 morning workouts before 7 AM',
    icon: 'Sunrise',
    date: 'Jun 10, 2026',
    rarity: 'rare'
  },
  {
    title: 'Consistency Champion',
    description: 'Maintained workout schedule for 6 consecutive months',
    icon: 'Calendar',
    date: 'Jun 1, 2026',
    rarity: 'rare'
  }];


  const handleExport = () => {
    alert('Progress data export functionality would download a comprehensive PDF report with all metrics, charts, and achievements.');
  };

  return (
    <>
      <Helmet>
        <title>Progress Analytics - FitWellness</title>
        <meta name="description" content="Track your fitness journey with comprehensive analytics, goal progress, personal records, and achievement tracking" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="mx-auto max-w-[1440px] px-4 md:px-6 lg:px-8 py-6 md:py-8 pb-24 lg:pb-8">
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-2">
              Progress Analytics
            </h1>
            <p className="text-base md:text-lg text-muted-foreground">
              Track your fitness journey and celebrate your achievements
            </p>
          </div>

          <div className="space-y-6 md:space-y-8">
            <FilterPanel
              selectedPeriod={selectedPeriod}
              onPeriodChange={setSelectedPeriod}
              selectedMetric={selectedMetric}
              onMetricChange={setSelectedMetric}
              onExport={handleExport} />


            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {keyMetrics?.map((metric, index) =>
              <MetricCard key={index} {...metric} />
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2 space-y-6 md:space-y-8">
                <ProgressChart
                  title="Workout Frequency"
                  data={workoutFrequencyData}
                  type="bar"
                  dataKeys={[
                  { dataKey: 'workouts', name: 'Completed' },
                  { dataKey: 'goal', name: 'Goal' }]
                  }
                  height={300} />


                <ProgressChart
                  title="Strength Progress (lbs)"
                  data={strengthProgressData}
                  type="line"
                  dataKeys={[
                  { dataKey: 'benchPress', name: 'Bench Press' },
                  { dataKey: 'squat', name: 'Squat' },
                  { dataKey: 'deadlift', name: 'Deadlift' }]
                  }
                  height={300}
                  colors={['var(--color-accent)', 'var(--color-primary)', 'var(--color-success)']} />


                <ProgressChart
                  title="Body Composition"
                  data={bodyCompositionData}
                  type="line"
                  dataKeys={[
                  { dataKey: 'weight', name: 'Weight (lbs)' },
                  { dataKey: 'bodyFat', name: 'Body Fat (%)' }]
                  }
                  height={300}
                  colors={['var(--color-primary)', 'var(--color-warning)']} />

              </div>

              <div className="space-y-6 md:space-y-8">
                <WorkoutStreakCard
                  currentStreak={23}
                  longestStreak={45}
                  weeklyGoal={5}
                  thisWeekWorkouts={3} />


                <BodyMeasurementTracker measurements={bodyMeasurements} />
              </div>
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4 md:mb-6">
                Active Goals
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {goals?.map((goal, index) =>
                <GoalProgressCard key={index} {...goal} />
                )}
              </div>
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4 md:mb-6">
                Personal Records
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {personalRecords?.map((record, index) =>
                <PersonalRecordCard key={index} {...record} />
                )}
              </div>
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4 md:mb-6">
                Recent Achievements
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {achievements?.map((achievement, index) =>
                <AchievementBadge key={index} {...achievement} />
                )}
              </div>
            </div>
          </div>
        </main>

        <MobileBottomNav />
        <QuickActionButton />
      </div>
    </>);

};

export default ProgressAnalytics;